/**
 * StudySync API Configuration
 * Centralized configuration for all API endpoints and settings
 */

import { FITBIT_REQUIRED_SCOPE } from "../constants/fitbitScope";

// Environment-based API configuration
const getApiBaseUrl = (): string => {
  // Use environment variable with fallback
  return process.env.REACT_APP_API_URL || "http://localhost:3002";
};

export const API_CONFIG = {
  BASE_URL: getApiBaseUrl(),
  TIMEOUT: 30000, // 30 seconds
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000, // 1 second
} as const;

// UI affordance only: the API remains the authority and mounts the scope
// upgrade route only for its matching historical authorization profile.
// Defaulting to false prevents an inactive/default environment from exposing
// a control that cannot safely do anything.
export const GOOGLE_HEALTH_UI_CONFIG = {
  SYNTHETIC_SCOPE_UPGRADE_ENABLED:
    process.env.REACT_APP_GOOGLE_HEALTH_SYNTHETIC_SCOPE_UPGRADE_ENABLED ===
    "true",
} as const;

// API Endpoints
export const API_ENDPOINTS = {
  // Authentication & Studies
  AUTH: {
    VERIFY_STUDY: "/api/auth/verify-study-code",
    VERIFY_STUDY_CODE: "/api/auth/verify-study-code",
    CREATE_STUDY: "/api/studies",
    RESEARCHER_STUDIES: "/api/auth/researcher-studies",
    COLLABORATIVE_STUDIES: "/api/auth/collaborative-studies",
    JOIN_STUDY: "/api/auth/join-study",
    UPDATE_STUDY: (studyId: string) => `/api/auth/update-study/${studyId}`,
    DELETE_STUDY: (studyId: string) => `/api/auth/delete-study/${studyId}`,
    NOTIFY_RESEARCHER: "/api/auth/notify-researcher",
  },

  // Studies Management
  STUDIES: {
    LIST: "/api/studies",
    GET: (studyId: string) => `/api/studies/${studyId}`,
    CREATE: "/api/studies",
    UPDATE: (studyId: string) => `/api/studies/${studyId}`,
    DELETE: (studyId: string) => `/api/studies/${studyId}`,
    PARTICIPANTS: (studyId: string) => `/api/studies/${studyId}/participants`,
    MEMBERS: (studyId: string) => `/api/studies/${studyId}/members`,
    STATS: (studyId: string) => `/api/studies/${studyId}/stats`,
  },

  // Participants
  PARTICIPANTS: {
    LIST: "/api/participants",
    GET: (participantId: string) => `/api/participants/${participantId}`,
    CREATE: "/api/participants",
    UPDATE: (participantId: string) => `/api/participants/${participantId}`,
    DELETE: (participantId: string) => `/api/participants/${participantId}`,
    DELETION_JOBS: (studyCode: string) =>
      `/api/participants/deletion-jobs?studyCode=${encodeURIComponent(studyCode)}`,
    VERIFY: "/api/participants/verify",
    VERIFY_BY_ID: (participantId: string) =>
      `/api/participants/${participantId}/verify`,
    ENROLL: "/api/participants/enroll",
    SECURE_VERIFY: "/api/secure-auth/send-email-verification",
  },

  // Fitbit Integration
  FITBIT: {
    AUTH_URL: "/api/fitbit/auth-url",
    PARTICIPANTS_NEEDING_REAUTH: "/api/fitbit/participants-needing-reauth",
    CALLBACK: "/api/fitbit/callback",
    REFRESH_TOKEN: "/api/fitbit/refresh-token",
    EXTRACT_DATA: "/api/fitbit/extract-data",
    REVOKE_ACCESS: "/api/fitbit/revoke-access",
    STATUS: (participantId: string) => `/api/fitbit/status/${participantId}`,
    MEMBER_SINCE: "/api/fitbit/member-since",
  },

  // Google Health authorization and the separately gated, read-only
  // synthetic probe. Neither endpoint starts ongoing collection.
  GOOGLE_HEALTH: {
    AUTHORIZATIONS: "/api/google-health/authorizations",
    ENROLLMENT_AUTHORIZATIONS: "/api/google-health/enrollment-authorizations",
    // This route is mounted only for the explicitly selected historical
    // scope profile. Clients must treat a 404 as unavailable rather than
    // attempting a fallback authorization flow.
    SYNTHETIC_SCOPE_UPGRADES: "/api/google-health/synthetic-scope-upgrades",
    REAUTHORIZATION_INVITATIONS: "/api/google-health/reauthorization-invitations",
    CONSUME_REAUTHORIZATION_INVITATION:
      "/api/google-health/reauthorization-invitations/consume",
    READ_PROBE: "/api/google-health/read-probe",
  },

  // Email & Communications
  EMAIL: {
    SEND_INVITATIONS: "/api/email/send-invitations",
    UPLOAD_ROSTER: "/api/email/upload-roster",
    HISTORY: (studyId: string) => `/api/email/history/${studyId}`,
  },

  // Verification
  VERIFICATION: {
    SEND_EMAIL_CODE: "/api/verification/send-email-code",
    VERIFY_EMAIL_CODE: "/api/verification/verify-email-code",
    SEND_PHONE_CODE: "/api/verification/send-phone-code",
    VERIFY_PHONE_CODE: "/api/verification/verify-phone-code",
    STATUS: (participantId: string) =>
      `/api/verification/status/${participantId}`,
  },

  // Analytics
  ANALYTICS: {
    SESSION_METRICS: "/api/analytics/session-metrics",
    EXPORT_METRICS: "/api/analytics/export-metrics",
  },

  // Adherence Monitoring
  ADHERENCE_MONITOR: {
    DATA: "/api/admin-adherence-monitor/data",
    STUDY_DISTRIBUTION: "/api/admin-adherence-monitor/study-distribution",
    ALERTS_ANALYTICS: "/api/admin-adherence-monitor/alerts-analysis",
    STUDIES_METRICS: "/api/admin-adherence-monitor/studies-metrics",
  },
  STUDY_ADHERENCE_MONITOR: {
    DATA: "/api/study-adherence-monitor/data",
    PARTICIPANT_DISTRIBUTION:
      "/api/study-adherence-monitor/participant-distribution",
    ALERTS_ANALYTICS: "/api/study-adherence-monitor/alerts-analysis",
    PARTICIPANTS_METRICS: "/api/study-adherence-monitor/participants-metrics",
    NON_ADHERENT_DAYS:
      "/api/study-adherence-monitor/participants/non-adherent-days",
  },
  STUDY_VISUALIZATIONS: {
    OVERVIEW: "/api/study-visualizations/overview",
    PARTICIPANT_OPTIONS: "/api/study-visualizations/participant-options",
    PARTICIPANTS: "/api/study-visualizations/participants",
    PARTICIPANT_DETAIL: "/api/study-visualizations/participant-detail",
  },

  CHAT: {
    ENSURE_CONVERSATIONS: (studyId: string) => `/api/chat/studies/${encodeURIComponent(studyId)}/conversations/ensure`,
    CONVERSATIONS: (studyId: string) => `/api/chat/studies/${encodeURIComponent(studyId)}/conversations`,
    MESSAGES: (studyId: string, conversationId: string) =>
      `/api/chat/studies/${encodeURIComponent(studyId)}/conversations/${encodeURIComponent(conversationId)}/messages`,
    CONVERSATION_KEYS: (studyId: string, conversationId: string) =>
      `/api/chat/studies/${encodeURIComponent(studyId)}/conversations/${encodeURIComponent(conversationId)}/keys`,
    KEY_GRANTS: (studyId: string, conversationId: string) =>
      `/api/chat/studies/${encodeURIComponent(studyId)}/conversations/${encodeURIComponent(conversationId)}/keys/grants`,
    ENCRYPTION_RESET: (studyId: string, conversationId: string) =>
      `/api/chat/studies/${encodeURIComponent(studyId)}/conversations/${encodeURIComponent(conversationId)}/encryption-reset`,
    READ_STATE: (studyId: string, conversationId: string) =>
      `/api/chat/studies/${encodeURIComponent(studyId)}/conversations/${encodeURIComponent(conversationId)}/read-state`,
    UNREAD: (studyId: string) => `/api/chat/studies/${encodeURIComponent(studyId)}/unread`,
    STREAM: (studyId: string) => `/api/chat/studies/${encodeURIComponent(studyId)}/stream`,
    PUBLISH_KEYS: '/api/chat/keys',
    MY_KEYS: '/api/chat/keys/me',
    RECOVERY_KEY: '/api/chat/keys/recovery',
    TRUST_EPOCH: '/api/chat/keys/trust-epoch',
    PASSKEY_REGISTER_OPTIONS: '/api/chat/passkeys/register/options',
    PASSKEY_REGISTER: '/api/chat/passkeys/register',
    PASSKEY_ASSERT_OPTIONS: '/api/chat/passkeys/assert/options',
    PASSKEY_ASSERT: '/api/chat/passkeys/assert',
    PASSKEYS: '/api/chat/passkeys',
    PASSKEY: (credentialId: string) => `/api/chat/passkeys/${encodeURIComponent(credentialId)}`,
    EVENTS: (studyId: string, conversationId: string) =>
      `/api/chat/studies/${encodeURIComponent(studyId)}/conversations/${encodeURIComponent(conversationId)}/events`,
  },

  // Support
  SUPPORT: {
    SUBMIT: '/api/support/submit',
    TICKETS: '/api/support/tickets',
    ADMIN_TICKETS: '/api/support/admin/tickets',
    TICKET: (id: string) => `/api/support/tickets/${encodeURIComponent(id)}`,
    SCREENSHOT: (id: string, index: number) => `/api/support/tickets/${encodeURIComponent(id)}/screenshots/${index}`,
    MESSAGES: (id: string, admin = false) => `/api/support/${admin ? 'admin/' : ''}tickets/${encodeURIComponent(id)}/messages`,
    STATUS: (id: string) => `/api/support/tickets/${encodeURIComponent(id)}/status`,
    READ: (id: string, admin = false) => `/api/support/${admin ? 'admin/' : ''}tickets/${encodeURIComponent(id)}/read`,
  },

  // Feature Spotlight
  FEATURES: {
    SEEN: '/api/features/seen',
  },

  DASHBOARD_UPDATES: {
    LIST: '/api/dashboard-updates',
    DETAIL: (updateId: string) => `/api/dashboard-updates/${updateId}`,
    UPLOAD_IMAGE: '/api/dashboard-updates/upload-image',
  },

  // Health Check
  HEALTH: "/health",
} as const;

// Fitbit OAuth Configuration
export const FITBIT_CONFIG = {
  CLIENT_ID: process.env.REACT_APP_FITBIT_CLIENT_ID || "",
  CLIENT_SECRET: process.env.REACT_APP_FITBIT_CLIENT_SECRET || "",
  REDIRECT_URI:
    process.env.REACT_APP_FITBIT_REDIRECT_URI ||
    `${
      process.env.REACT_APP_CLIENT_URL || "http://localhost:3000"
    }/fitbit/callback`,
  AUTHORIZATION_URL: "https://www.fitbit.com/oauth2/authorize",
  TOKEN_URL: "https://api.fitbit.com/oauth2/token",
  SCOPE: FITBIT_REQUIRED_SCOPE,
};

// Firebase configuration for frontend (public - safe to expose)
export const FIREBASE_CONFIG = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "",
} as const;

// Request configuration defaults
export const REQUEST_CONFIG = {
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  mode: "cors" as RequestMode,
  credentials: "omit" as RequestCredentials,
} as const;

// API Service utility
class ApiService {
  private baseUrl: string;
  private timeout: number;

  constructor() {
    this.baseUrl = API_CONFIG.BASE_URL;
    this.timeout = API_CONFIG.TIMEOUT;
  }

  async getAuthToken(): Promise<string | null> {
    // Get token from secure auth service
    const { secureAuthService } = await import("../services/secureAuthService");
    return secureAuthService.getValidToken();
  }

  private async makeRequest(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<any> {
    const token = await this.getAuthToken();

    const config: RequestInit = {
      ...REQUEST_CONFIG,
      ...options,
      headers: {
        ...REQUEST_CONFIG.headers,
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    };

    const response = await fetch(`${this.baseUrl}${endpoint}`, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw Object.assign(new Error(
        errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      ), {
        status: response.status,
        code: typeof errorData.code === 'string' ? errorData.code : undefined,
        requestId: errorData.requestId || errorData.referenceId,
      });
    }

    if (response.status === 204) return null;
    const responseData = await response.json();
    return responseData;
  }

  async get(endpoint: string): Promise<any> {
    return this.makeRequest(endpoint, { method: "GET" });
  }

  async post(endpoint: string, data?: any): Promise<any> {
    return this.makeRequest(endpoint, {
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put(endpoint: string, data?: any): Promise<any> {
    return this.makeRequest(endpoint, {
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch(endpoint: string, data?: any): Promise<any> {
    return this.makeRequest(endpoint, {
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete(endpoint: string): Promise<any> {
    return this.makeRequest(endpoint, { method: "DELETE" });
  }
}

export const apiService = new ApiService();

export default API_CONFIG;
